import { useEffect, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import axios from "axios";
import { Link } from "react-router-dom";
import { readStaleSessionCache, writeSessionCache } from "@/lib/sessionCache";
import { SkeletonTable } from "@/components/Skeletons";
import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Package, CalendarDays, Truck, AlertTriangle, CheckCircle, Wrench, Users as UsersIcon, FileText, Edit, Trash2, Warehouse, Send } from "lucide-react";
import { formatDataItaliana } from "@/utils/dateUtils";
import { useYear } from "@/contexts/YearContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CommessePosePage({ user, logout }) {
  const { selectedYear } = useYear();
  const _cached = readStaleSessionCache(`commesse_pose_${selectedYear}`);
  const { mutate } = useSWRConfig();

  // SWR keys
  const commesseKey = `/commesse-pose?year=${selectedYear}`;
  const posatoriKey = `/posatori`;
  const trasportiKey = `/trasporti`;
  const articoliKey = `/tipo-articoli`;
  const clientiKey = `/pagamenti/clienti?year=${selectedYear}`;
  const magazziniKey = `/magazzini`;
  const intervisteKey = `/interviews?year=${selectedYear}&light=true`;

  const { data: swrCommesse } = useSWR(commesseKey, { fallbackData: _cached?.commesse });
  const { data: swrPosatori } = useSWR(posatoriKey, { fallbackData: _cached?.posatori });
  const { data: swrTrasporti } = useSWR(trasportiKey, { fallbackData: _cached?.trasporti });
  const { data: swrArticoli } = useSWR(articoliKey, { fallbackData: _cached?.tipoArticoli });
  const { data: swrClienti } = useSWR(clientiKey, { fallbackData: _cached?.clientiPagamenti });
  const { data: swrMagazzini } = useSWR(magazziniKey, { fallbackData: _cached?.magazzini });
  const { data: swrInterviste } = useSWR(intervisteKey);

  const [commesse, setCommesse] = useState(swrCommesse || _cached?.commesse || []);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showStats, setShowStats] = useState(false);
  const [loading, setLoading] = useState(!_cached && !swrCommesse);
  
  // Gestione Posatori
  const [posatori, setPosatori] = useState(swrPosatori || _cached?.posatori || []);
  const [showPosatoriDialog, setShowPosatoriDialog] = useState(false);
  const [posatoreForm, setPosatoreForm] = useState({ nome: "", cognome: "", indirizzo: "", telefono: "", email: "" });
  const [editingPosatore, setEditingPosatore] = useState(null);
  
  // Gestione Trasporti
  const [trasporti, setTrasporti] = useState(swrTrasporti || _cached?.trasporti || []);
  const [showTrasportiDialog, setShowTrasportiDialog] = useState(false);
  const [trasportoForm, setTrasportoForm] = useState({ nome: "", cognome: "", indirizzo: "", telefono: "", email: "" });
  const [editingTrasporto, setEditingTrasporto] = useState(null);
  
  // Gestione Magazzini
  const [magazzini, setMagazzini] = useState(swrMagazzini || _cached?.magazzini || []);
  const [showMagazziniDialog, setShowMagazziniDialog] = useState(false);
  const [magazzinoForm, setMagazzinoForm] = useState({ nome: "", indirizzo: "", telefono: "" });
  const [editingMagazzino, setEditingMagazzino] = useState(null);
  
  // Gestione Articoli
  const [tipoArticoli, setTipoArticoli] = useState(swrArticoli || _cached?.tipoArticoli || []);
  const [showArticoliDialog, setShowArticoliDialog] = useState(false);
  const [articoloForm, setArticoloForm] = useState({ nome: "" });
  const [editingArticolo, setEditingArticolo] = useState(null);
  
  // Nuova Commessa
  const [showNewCommessaDialog, setShowNewCommessaDialog] = useState(false);
  const [intervisteVinte, setIntervisteVinte] = useState([]);
  const [clientiPagamenti, setClientiPagamenti] = useState(swrClienti || _cached?.clientiPagamenti || []);
  const [clienteManuale, setClienteManuale] = useState(false);
  const [selectedClientePagamenti, setSelectedClientePagamenti] = useState(null);
  // Ricerca cliente nel dialog "Crea Nuova Commessa"
  const [clienteSearchTerm, setClienteSearchTerm] = useState("");
  const [newCommessaForm, setNewCommessaForm] = useState({
    intervista_id: null,
    cliente_nome: "",
    cliente_cognome: "",
    cliente_telefono: "",
    indirizzo: "",
    articoli: []
  });

  // Calcola statistiche dalle commesse caricate
  const isCompletata = (c) => c.posato === true || c.completata === true;

  const calculateStats = () => {
    const today = new Date().toISOString().split('T')[0];
    
    // Da Trasmettere: commesse con almeno un articolo non trasmesso (trasmesso = false)
    const daTrasmettere = commesse.filter(c => {
      if (!c.articoli || c.articoli.length === 0) return false;
      return c.articoli.some(a => !a.trasmesso);
    }).length;
    
    // Tutto in Deposito: commesse dove TUTTI gli articoli hanno data_arrivo_deposito
    // MA escludi le commesse già posate/completate
    const tuttoInDeposito = commesse.filter(c => {
      if (isCompletata(c)) return false; // Escludi commesse già posate/completate
      if (!c.articoli || c.articoli.length === 0) return false;
      return c.articoli.every(a => a.data_arrivo_deposito && a.data_arrivo_deposito !== "");
    }).length;
    
    // Deposito Parziale: commesse dove ALCUNI (ma non tutti) articoli hanno data_arrivo_deposito
    const depositoParziale = commesse.filter(c => {
      if (isCompletata(c)) return false;
      if (!c.articoli || c.articoli.length === 0) return false;
      const conData = c.articoli.filter(a => a.data_arrivo_deposito && a.data_arrivo_deposito !== "").length;
      return conData > 0 && conData < c.articoli.length;
    }).length;
    
    // Programmati: posatore + data posa valorizzata + data FUTURA (esclude completate)
    const programmati = commesse.filter(c => {
      if (isCompletata(c)) return false;
      const hasPosatore = c.posatore_id || c.posatore_nome;
      const hasDataPosa = c.data_posa && c.data_posa !== "";
      if (!hasPosatore || !hasDataPosa) return false;
      return c.data_posa > today;
    }).length;
    
    // In Posa: posatore + data posa valorizzata + data = OGGI (esclude completate)
    const inPosa = commesse.filter(c => {
      if (isCompletata(c)) return false;
      const hasPosatore = c.posatore_id || c.posatore_nome;
      const hasDataPosa = c.data_posa && c.data_posa !== "";
      if (!hasPosatore || !hasDataPosa) return false;
      return c.data_posa === today;
    }).length;
    
    // Finiti: commesse con posato=true OPPURE completata=true (chiusura commessa)
    const finiti = commesse.filter(c => isCompletata(c)).length;
    
    // Scadute: data posa nel passato E non posato/completata E non posa_incompleta
    const scadute = commesse.filter(c => {
      const hasDataPosa = c.data_posa && c.data_posa !== "";
      if (!hasDataPosa) return false;
      if (isCompletata(c)) return false;
      if (c.posa_incompleta === true) return false;
      return c.data_posa < today;
    }).length;
    
    return {
      da_trasmettere: daTrasmettere,
      tutto_in_deposito: tuttoInDeposito,
      deposito_parziale: depositoParziale,
      programmati,
      in_posa: inPosa,
      finiti,
      scadute
    };
  };
  
  const stats = calculateStats();

  // Sync SWR → local state (mantiene compatibilità con le mutazioni esistenti)
  useEffect(() => { if (swrCommesse) setCommesse(swrCommesse); }, [swrCommesse]);
  useEffect(() => { if (swrPosatori) setPosatori(swrPosatori); }, [swrPosatori]);
  useEffect(() => { if (swrTrasporti) setTrasporti(swrTrasporti); }, [swrTrasporti]);
  useEffect(() => { if (swrArticoli) setTipoArticoli(swrArticoli); }, [swrArticoli]);
  useEffect(() => { if (swrClienti) setClientiPagamenti(swrClienti); }, [swrClienti]);
  useEffect(() => { if (swrMagazzini) setMagazzini(swrMagazzini); }, [swrMagazzini]);
  useEffect(() => {
    if (swrInterviste) {
      const vinte = swrInterviste.filter(i => i.esito === "vinto" && i.stato === "Consegnato");
      setIntervisteVinte(vinte);
    }
  }, [swrInterviste]);
  useEffect(() => { if (swrCommesse && loading) setLoading(false); }, [swrCommesse, loading]);

  // Post-mutazione: invalida tutti gli endpoint SWR
  const fetchData = async () => {
    await Promise.all([
      mutate(commesseKey),
      mutate(posatoriKey),
      mutate(trasportiKey),
      mutate(articoliKey),
      mutate(clientiKey),
      mutate(magazziniKey),
    ]);
    writeSessionCache(`commesse_pose_${selectedYear}`, {
      commesse, posatori, trasporti, tipoArticoli, clientiPagamenti, magazzini,
    });
  };

  const fetchIntervisteVinte = () => mutate(intervisteKey);

  // Posatori CRUD
  const handleSavePosatore = async (e) => {
    e.preventDefault();
    try {
      if (editingPosatore) {
        await axios.put(`${API}/posatori/${editingPosatore.id}`, posatoreForm, { withCredentials: true });
        toast.success("Posatore aggiornato");
      } else {
        await axios.post(`${API}/posatori`, posatoreForm, { withCredentials: true });
        toast.success("Posatore creato");
      }
      setPosatoreForm({ nome: "", cognome: "", indirizzo: "", telefono: "", email: "" });
      setEditingPosatore(null);
      setShowPosatoriDialog(false);
      fetchData();
    } catch (e) {
      toast.error("Errore nel salvataggio del posatore");
    }
  };

  const handleDeletePosatore = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo posatore?")) return;
    try {
      await axios.delete(`${API}/posatori/${id}`, { withCredentials: true });
      toast.success("Posatore eliminato");
      fetchData();
    } catch (e) {
      toast.error("Errore nell'eliminazione del posatore");
    }
  };

  // Trasporti CRUD
  const handleSaveTrasporto = async (e) => {
    e.preventDefault();
    try {
      if (editingTrasporto) {
        await axios.put(`${API}/trasporti/${editingTrasporto.id}`, trasportoForm, { withCredentials: true });
        toast.success("Trasporto aggiornato");
      } else {
        await axios.post(`${API}/trasporti`, trasportoForm, { withCredentials: true });
        toast.success("Trasporto creato");
      }
      setTrasportoForm({ nome: "", cognome: "", indirizzo: "", telefono: "", email: "" });
      setEditingTrasporto(null);
      setShowTrasportiDialog(false);
      fetchData();
    } catch (e) {
      toast.error("Errore nel salvataggio del trasporto");
    }
  };

  const handleDeleteTrasporto = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo trasporto?")) return;
    try {
      await axios.delete(`${API}/trasporti/${id}`, { withCredentials: true });
      toast.success("Trasporto eliminato");
      fetchData();
    } catch (e) {
      toast.error("Errore nell'eliminazione del trasporto");
    }
  };

  // Magazzini CRUD
  const handleSaveMagazzino = async (e) => {
    e.preventDefault();
    try {
      if (editingMagazzino) {
        await axios.put(`${API}/magazzini/${editingMagazzino.id}`, magazzinoForm, { withCredentials: true });
        toast.success("Magazzino aggiornato");
      } else {
        await axios.post(`${API}/magazzini`, magazzinoForm, { withCredentials: true });
        toast.success("Magazzino creato");
      }
      setMagazzinoForm({ nome: "", indirizzo: "", telefono: "" });
      setEditingMagazzino(null);
      setShowMagazziniDialog(false);
      fetchData();
    } catch (e) {
      toast.error("Errore nel salvataggio del magazzino");
    }
  };

  const handleDeleteMagazzino = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo magazzino?")) return;
    try {
      await axios.delete(`${API}/magazzini/${id}`, { withCredentials: true });
      toast.success("Magazzino eliminato");
      fetchData();
    } catch (e) {
      toast.error("Errore nell'eliminazione del magazzino");
    }
  };

  // Articoli CRUD
  const handleSaveArticolo = async (e) => {
    e.preventDefault();
    try {
      if (editingArticolo) {
        await axios.put(`${API}/tipo-articoli/${editingArticolo.id}`, articoloForm, { withCredentials: true });
        toast.success("Articolo aggiornato");
      } else {
        await axios.post(`${API}/tipo-articoli`, articoloForm, { withCredentials: true });
        toast.success("Articolo creato");
      }
      setArticoloForm({ nome: "" });
      setEditingArticolo(null);
      setShowArticoliDialog(false);
      fetchData();
    } catch (e) {
      toast.error("Errore nel salvataggio dell'articolo");
    }
  };

  const handleDeleteArticolo = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo tipo di articolo?")) return;
    try {
      await axios.delete(`${API}/tipo-articoli/${id}`, { withCredentials: true });
      toast.success("Articolo eliminato");
      fetchData();
    } catch (e) {
      toast.error("Errore nell'eliminazione dell'articolo");
    }
  };

  // Crea Nuova Commessa
  const handleCreateCommessa = async () => {
    try {
      await axios.post(`${API}/commesse-pose`, {
        cliente_nome: newCommessaForm.cliente_nome,
        cliente_cognome: newCommessaForm.cliente_cognome,
        cliente_telefono: newCommessaForm.cliente_telefono || "",
        indirizzo: newCommessaForm.indirizzo || "",
        articoli: newCommessaForm.articoli.map(a => ({
          tipo_articolo_id: a.tipo_articolo_id,
          nome_articolo: a.tipo_articolo_nome,
          trasmesso: a.trasmesso,
          data_prevista_ordine: a.data_prevista_ordine || "",
          data_arrivo_deposito: a.data_arrivo_deposito || ""
        })),
        note: clienteManuale ? "Cliente inserito manualmente - verificare dati pagamenti" : "Cliente da anagrafica pagamenti"
      }, { withCredentials: true });
      toast.success("Commessa creata con successo");
      setShowNewCommessaDialog(false);
      setClienteManuale(false);
      setSelectedClientePagamenti(null);
      setNewCommessaForm({
        intervista_id: null,
        cliente_nome: "",
        cliente_cognome: "",
        cliente_telefono: "",
        indirizzo: "",
        articoli: []
      });
      fetchData();
    } catch (e) {
      console.error("Error creating commessa:", e);
      toast.error(e.response?.data?.detail || "Errore nella creazione della commessa");
    }
  };

  // Filtri
  const getFilteredCommesse = () => {
    let filtered = commesse;
    const today = new Date().toISOString().split('T')[0];
    
    if (filter === "da_trasmettere") {
      // Commesse con almeno un articolo non trasmesso
      filtered = filtered.filter(c => {
        if (!c.articoli || c.articoli.length === 0) return false;
        return c.articoli.some(a => !a.trasmesso);
      });
    } else if (filter === "tutto_in_deposito") {
      // Tutte le commesse dove TUTTI gli articoli hanno data_arrivo_deposito valorizzato
      // MA escludi le commesse già posate/completate
      filtered = filtered.filter(c => {
        if (isCompletata(c)) return false; // Escludi commesse già posate/completate
        if (!c.articoli || c.articoli.length === 0) return false;
        return c.articoli.every(a => a.data_arrivo_deposito && a.data_arrivo_deposito !== "");
      });
    } else if (filter === "deposito_parziale") {
      // Commesse dove ALMENO UNO (ma non tutti) degli articoli ha data_arrivo_deposito valorizzato
      filtered = filtered.filter(c => {
        if (isCompletata(c)) return false;
        if (!c.articoli || c.articoli.length === 0) return false;
        const conData = c.articoli.filter(a => a.data_arrivo_deposito && a.data_arrivo_deposito !== "").length;
        return conData > 0 && conData < c.articoli.length;
      });
    } else if (filter === "programmati") {
      // Posatore + data posa valorizzata + data FUTURA (> oggi), esclude completate
      filtered = filtered.filter(c => {
        if (isCompletata(c)) return false;
        const hasPosatore = c.posatore_id || c.posatore_nome;
        const hasDataPosa = c.data_posa && c.data_posa !== "";
        if (!hasPosatore || !hasDataPosa) return false;
        return c.data_posa > today;
      });
    } else if (filter === "in_posa") {
      // Posatore + data posa valorizzata + data = OGGI, esclude completate
      filtered = filtered.filter(c => {
        if (isCompletata(c)) return false;
        const hasPosatore = c.posatore_id || c.posatore_nome;
        const hasDataPosa = c.data_posa && c.data_posa !== "";
        if (!hasPosatore || !hasDataPosa) return false;
        return c.data_posa === today;
      });
    } else if (filter === "finiti") {
      // Commesse con flag posato=true OPPURE completata=true (chiusura commessa)
      filtered = filtered.filter(c => isCompletata(c));
    } else if (filter === "scadute") {
      // Data posa nel passato + non posato/completata + non posa_incompleta
      filtered = filtered.filter(c => {
        const hasDataPosa = c.data_posa && c.data_posa !== "";
        if (!hasDataPosa) return false;
        if (isCompletata(c)) return false;
        if (c.posa_incompleta === true) return false;
        return c.data_posa < today;
      });
    }
    // Se filter === "all", non applicare filtri
    
    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.cliente_nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.cliente_cognome.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  };

  // Ritorna badge per Status Ordine (basato su trasmesso)
  const getOrdineStatusBadge = (articoli) => {
    if (!articoli || articoli.length === 0) {
      return <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">N/A</span>;
    }
    
    const trasmessi = articoli.filter(a => a.trasmesso).length;
    const totali = articoli.length;
    
    if (trasmessi === 0) {
      return <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">Da trasmettere</span>;
    } else if (trasmessi < totali) {
      return <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Parzialmente Trasmesso</span>;
    } else {
      return <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">Trasmesso</span>;
    }
  };

  // Ritorna badge per Status Magazzino (basato su date arrivo deposito e stato posa)
  const getMagazzinoStatusBadge = (articoli, commessa) => {
    // Se la commessa è stata posata, mostra "Posata"
    if (commessa?.posato) {
      return <span className="px-2 py-1 rounded text-xs font-medium bg-emerald-100 text-emerald-800">✓ Posata</span>;
    }
    
    // Se la posa è incompleta
    if (commessa?.posa_incompleta) {
      return <span className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">Posa parziale</span>;
    }
    
    if (!articoli || articoli.length === 0) {
      return <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">N/A</span>;
    }
    
    const conDataArrivo = articoli.filter(a => a.data_arrivo_deposito && a.data_arrivo_deposito !== "").length;
    const totali = articoli.length;
    
    if (conDataArrivo === 0) {
      return <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">Non arrivato</span>;
    } else if (conDataArrivo < totali) {
      return <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Parzialmente arrivato</span>;
    } else {
      return <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">Arrivato</span>;
    }
  };

  // Ritorna conteggio articoli
  const getArticoliCount = (articoli) => {
    if (!articoli || !Array.isArray(articoli)) return 0;
    return articoli.length;
  };

  // Ritorna badge per Status Avvisi Cliente
  const getAvvisiClienteBadge = (status) => {
    const badges = {
      da_avvisare: { label: "Da Avvisare", color: "bg-red-100 text-red-800" },
      chiamato_non_risposto: { label: "Chiamato", color: "bg-yellow-100 text-yellow-800" },
      avvisato: { label: "Avvisato", color: "bg-green-100 text-green-800" }
    };
    const badge = badges[status] || badges.da_avvisare;
    return <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>{badge.label}</span>;
  };

  // Verifica logistica completa
  const verificaLogistica = (commessa) => {
    const hasPostore = commessa?.posatore_id || commessa?.posatore_nome;
    const hasDataPosa = commessa?.data_posa;
    const hasTrasporto = commessa?.facchinaggio_id || commessa?.facchinaggio_nome;
    const hasDataTrasporto = commessa?.data_trasporto;
    
    return {
      completa: hasPostore && hasDataPosa && hasTrasporto && hasDataTrasporto,
      hasPostore,
      hasDataPosa,
      hasTrasporto,
      hasDataTrasporto
    };
  };

  // Badge per Alert Logistica
  const getLogisticaAlertBadge = (commessa) => {
    // Verifica stato posa
    if (commessa.posato) {
      return <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">Posa 100%</span>;
    }
    
    if (commessa.posa_incompleta) {
      return <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Posa 50%</span>;
    }
    
    // Verifica logistica (solo se non è posato né incompleto)
    const logistica = verificaLogistica(commessa);
    
    if (logistica.completa) {
      return <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">Logistica OK</span>;
    } else {
      return <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">Verificare la Logistica</span>;
    }
  };

  if (loading) {
    return (
      <PageLayout user={user} logout={logout} testId="commesse-pose-page" className="overflow-y-auto">
        <div className="p-6 max-w-7xl mx-auto">
          <div className="mb-6">
            <div className="h-8 bg-slate-200 rounded w-72 animate-pulse mb-2" />
            <div className="h-4 bg-slate-100 rounded w-96 animate-pulse" />
          </div>
          <SkeletonTable rows={8} cols={5} />
        </div>
      </PageLayout>
    );
  }

  const filteredCommesse = getFilteredCommesse();

  return (
    <PageLayout user={user} logout={logout} testId="commesse-pose-page" className="overflow-y-auto">
        <div className="p-4 md:p-6">
          {/* Header - Compatto */}
          <div className="mb-4">
            <div className="flex flex-col gap-3 mb-3">
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Pose e Magazzino</h1>
                <p className="text-sm text-slate-600 hidden lg:block">Gestione commesse, articoli e programmazione pose</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowPosatoriDialog(true)} className="flex-1 sm:flex-none">
                  <UsersIcon className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Gestisci </span>Posatori
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowTrasportiDialog(true)} className="flex-1 sm:flex-none">
                  <Wrench className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Gestisci </span>Trasporto
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowMagazziniDialog(true)} className="flex-1 sm:flex-none">
                  <Warehouse className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Gestisci </span>Magazzini
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowArticoliDialog(true)} className="flex-1 sm:flex-none">
                  <FileText className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Gestisci </span>Articoli
                </Button>
                <Button className="bg-blue-600 hover:bg-blue-700 flex-1 sm:flex-none" size="sm" onClick={() => setShowNewCommessaDialog(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Nuova Commessa
                </Button>
              </div>
            </div>
          </div>

          {/* Filtri e Ricerca - Compatto */}
          <Card className="mb-4">
            <CardContent className="py-3">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Cerca per nome cliente..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-9"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Statistiche Dashboard - Tab Filtro */}
          <div className="grid grid-cols-4 md:grid-cols-8 gap-2 lg:gap-4 mb-4">
            <Card 
              className={`cursor-pointer hover:shadow-md transition-shadow ${filter === "scadute" ? "ring-2 ring-red-500 bg-red-50" : (stats.scadute > 0 ? "border-red-300" : "")}`} 
              onClick={() => setFilter("scadute")}
              data-testid="card-pose-scadute"
            >
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className={`w-4 h-4 ${stats.scadute > 0 ? "text-red-600 animate-pulse" : "text-slate-400"}`} />
                    <span className="text-xs text-slate-600 hidden lg:inline">Scadute</span>
                  </div>
                  <span className={`text-lg font-bold ${stats.scadute > 0 ? "text-red-600" : "text-slate-400"}`}>{stats.scadute}</span>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer hover:shadow-md transition-shadow ${filter === "da_trasmettere" ? "ring-2 ring-purple-500 bg-purple-50" : ""}`} 
              onClick={() => setFilter("da_trasmettere")}
            >
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Send className="w-4 h-4 text-purple-600" />
                    <span className="text-xs text-slate-600 hidden lg:inline">Da Trasmettere</span>
                  </div>
                  <span className="text-lg font-bold text-purple-600">{stats.da_trasmettere}</span>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer hover:shadow-md transition-shadow ${filter === "tutto_in_deposito" ? "ring-2 ring-green-500 bg-green-50" : ""}`} 
              onClick={() => setFilter("tutto_in_deposito")}
            >
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-green-600" />
                    <span className="text-xs text-slate-600 hidden lg:inline">Tutto in Deposito</span>
                  </div>
                  <span className="text-lg font-bold text-green-600">{stats.tutto_in_deposito}</span>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer hover:shadow-md transition-shadow ${filter === "deposito_parziale" ? "ring-2 ring-orange-500 bg-orange-50" : ""}`} 
              onClick={() => setFilter("deposito_parziale")}
            >
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-orange-600" />
                    <span className="text-xs text-slate-600 hidden lg:inline">In Deposito Parziale</span>
                  </div>
                  <span className="text-lg font-bold text-orange-600">{stats.deposito_parziale}</span>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer hover:shadow-md transition-shadow ${filter === "programmati" ? "ring-2 ring-blue-500 bg-blue-50" : ""}`} 
              onClick={() => setFilter("programmati")}
            >
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-blue-600" />
                    <span className="text-xs text-slate-600 hidden lg:inline">Programmati</span>
                  </div>
                  <span className="text-lg font-bold text-blue-600">{stats.programmati}</span>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer hover:shadow-md transition-shadow ${filter === "in_posa" ? "ring-2 ring-amber-500 bg-amber-50" : ""}`} 
              onClick={() => setFilter("in_posa")}
            >
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-amber-600" />
                    <span className="text-xs text-slate-600 hidden lg:inline">In Posa</span>
                  </div>
                  <span className="text-lg font-bold text-amber-600">{stats.in_posa}</span>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer hover:shadow-md transition-shadow ${filter === "finiti" ? "ring-2 ring-emerald-500 bg-emerald-50" : ""}`} 
              onClick={() => setFilter("finiti")}
            >
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs text-slate-600 hidden lg:inline">Finiti</span>
                  </div>
                  <span className="text-lg font-bold text-emerald-600">{stats.finiti}</span>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer hover:shadow-md transition-shadow ${filter === "all" ? "ring-2 ring-slate-500 bg-slate-50" : ""}`} 
              onClick={() => setFilter("all")}
            >
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-slate-600" />
                    <span className="text-xs text-slate-600 hidden lg:inline">Mostra Tutti</span>
                  </div>
                  <span className="text-lg font-bold text-slate-600">{commesse.length}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabella Commesse */}
          <Card>
            <CardHeader>
              <CardTitle>Commesse ({filteredCommesse.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredCommesse.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Nessuna commessa trovata</p>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-4 md:mx-0">
                  <table className="w-full min-w-[1200px]">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 md:p-3 text-xs md:text-sm font-semibold text-slate-700">Cliente</th>
                        <th className="text-center p-2 md:p-3 text-xs md:text-sm font-semibold text-slate-700">Articoli</th>
                        <th className="text-left p-2 md:p-3 text-xs md:text-sm font-semibold text-slate-700">Ordine</th>
                        <th className="text-left p-2 md:p-3 text-xs md:text-sm font-semibold text-slate-700">Status Magazzino</th>
                        <th className="text-left p-2 md:p-3 text-xs md:text-sm font-semibold text-slate-700 hidden md:table-cell">Posatore</th>
                        <th className="text-left p-2 md:p-3 text-xs md:text-sm font-semibold text-slate-700 hidden xl:table-cell">Data Posa</th>
                        <th className="text-left p-2 md:p-3 text-xs md:text-sm font-semibold text-slate-700 hidden lg:table-cell">Trasporto</th>
                        <th className="text-left p-2 md:p-3 text-xs md:text-sm font-semibold text-slate-700 hidden xl:table-cell">Data Trasporto</th>
                        <th className="text-left p-2 md:p-3 text-xs md:text-sm font-semibold text-slate-700 hidden lg:table-cell">Avvisi Cliente</th>
                        <th className="text-left p-2 md:p-3 text-xs md:text-sm font-semibold text-slate-700">Alert</th>
                        <th className="text-center p-2 md:p-3 text-xs md:text-sm font-semibold text-slate-700">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCommesse.map((commessa) => {
                        const logistica = verificaLogistica(commessa);
                        const rowClass = logistica.completa 
                          ? "border-b hover:bg-green-50 bg-green-50/30" 
                          : "border-b hover:bg-red-50 bg-red-50/30";
                        
                        return (
                        <tr key={commessa.id} className={rowClass}>
                          <td className="p-2 md:p-3">
                            <div>
                              <p className="font-medium text-sm md:text-base text-slate-900">{commessa.cliente_nome} {commessa.cliente_cognome}</p>
                              <p className="text-xs text-slate-500">{commessa.cliente_telefono}</p>
                            </div>
                          </td>
                          <td className="p-2 md:p-3 text-center">
                            <span className="font-bold text-base text-slate-900">{getArticoliCount(commessa.articoli)}</span>
                          </td>
                          <td className="p-2 md:p-3">{getOrdineStatusBadge(commessa.articoli)}</td>
                          <td className="p-2 md:p-3">{getMagazzinoStatusBadge(commessa.articoli, commessa)}</td>
                          <td className="p-2 md:p-3 text-xs md:text-sm text-slate-600 hidden md:table-cell">
                            {commessa?.posatore_nome ? (
                              <div className="whitespace-pre-line">
                                {commessa.posatore_nome}
                              </div>
                            ) : "-"}
                          </td>
                          <td className="p-2 md:p-3 text-xs md:text-sm text-slate-600 hidden xl:table-cell">
                            {commessa?.data_posa ? `${formatDataItaliana(commessa.data_posa)} ${commessa?.ora_posa || ""}` : "-"}
                          </td>
                          <td className="p-2 md:p-3 text-xs md:text-sm text-slate-600 hidden lg:table-cell">{commessa?.facchinaggio_nome || "-"}</td>
                          <td className="p-2 md:p-3 text-xs md:text-sm text-slate-600 hidden xl:table-cell">
                            {commessa?.data_trasporto ? `${formatDataItaliana(commessa.data_trasporto)} ${commessa?.ora_trasporto || ""}` : "-"}
                          </td>
                          <td className="p-2 md:p-3 hidden lg:table-cell">
                            {getAvvisiClienteBadge(commessa?.status_avviso_cliente || "da_avvisare")}
                          </td>
                          <td className="p-2 md:p-3">
                            {getLogisticaAlertBadge(commessa)}
                          </td>
                          <td className="p-2 md:p-3 text-center">
                            <Link to={`/commesse-pose/${commessa.id}`}>
                              <Button size="sm" variant="outline" className="text-xs">
                                Dettagli
                              </Button>
                            </Link>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dialog Gestione Posatori */}
          <Dialog open={showPosatoriDialog} onOpenChange={setShowPosatoriDialog}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Gestione Posatori</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <form onSubmit={handleSavePosatore} className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Nome *"
                      value={posatoreForm.nome}
                      onChange={(e) => setPosatoreForm({ ...posatoreForm, nome: e.target.value })}
                      required
                    />
                    <Input
                      placeholder="Cognome *"
                      value={posatoreForm.cognome}
                      onChange={(e) => setPosatoreForm({ ...posatoreForm, cognome: e.target.value })}
                      required
                    />
                  </div>
                  <Input
                    placeholder="Indirizzo"
                    value={posatoreForm.indirizzo}
                    onChange={(e) => setPosatoreForm({ ...posatoreForm, indirizzo: e.target.value })}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Telefono"
                      value={posatoreForm.telefono}
                      onChange={(e) => setPosatoreForm({ ...posatoreForm, telefono: e.target.value })}
                    />
                    <Input
                      placeholder="Email"
                      type="email"
                      value={posatoreForm.email}
                      onChange={(e) => setPosatoreForm({ ...posatoreForm, email: e.target.value })}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1">{editingPosatore ? "Aggiorna" : "Aggiungi"}</Button>
                    {editingPosatore && (
                      <Button type="button" variant="outline" onClick={() => { setEditingPosatore(null); setPosatoreForm({ nome: "", cognome: "", indirizzo: "", telefono: "", email: "" }); }}>
                        Annulla
                      </Button>
                    )}
                  </div>
                </form>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {posatori.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                      <div>
                        <p className="font-medium">{p.nome} {p.cognome}</p>
                        {p.telefono && <p className="text-sm text-slate-600">Tel: {p.telefono}</p>}
                        {p.email && <p className="text-sm text-slate-600">Email: {p.email}</p>}
                        {p.indirizzo && <p className="text-sm text-slate-500">{p.indirizzo}</p>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => { 
                          setEditingPosatore(p); 
                          setPosatoreForm({ 
                            nome: p.nome || "", 
                            cognome: p.cognome || "", 
                            indirizzo: p.indirizzo || "", 
                            telefono: p.telefono || "", 
                            email: p.email || "" 
                          }); 
                        }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDeletePosatore(p.id)}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog Gestione Trasporti */}
          <Dialog open={showTrasportiDialog} onOpenChange={setShowTrasportiDialog}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Gestione Trasporti/Facchinaggio</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <form onSubmit={handleSaveTrasporto} className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Nome / Ragione Sociale *"
                      value={trasportoForm.nome}
                      onChange={(e) => setTrasportoForm({ ...trasportoForm, nome: e.target.value })}
                      required
                    />
                    <Input
                      placeholder="Cognome (opzionale)"
                      value={trasportoForm.cognome}
                      onChange={(e) => setTrasportoForm({ ...trasportoForm, cognome: e.target.value })}
                    />
                  </div>
                  <Input
                    placeholder="Indirizzo"
                    value={trasportoForm.indirizzo}
                    onChange={(e) => setTrasportoForm({ ...trasportoForm, indirizzo: e.target.value })}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Telefono"
                      value={trasportoForm.telefono}
                      onChange={(e) => setTrasportoForm({ ...trasportoForm, telefono: e.target.value })}
                    />
                    <Input
                      placeholder="Email"
                      type="email"
                      value={trasportoForm.email}
                      onChange={(e) => setTrasportoForm({ ...trasportoForm, email: e.target.value })}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1">{editingTrasporto ? "Aggiorna" : "Aggiungi"}</Button>
                    {editingTrasporto && (
                      <Button type="button" variant="outline" onClick={() => { setEditingTrasporto(null); setTrasportoForm({ nome: "", cognome: "", indirizzo: "", telefono: "", email: "" }); }}>
                        Annulla
                      </Button>
                    )}
                  </div>
                </form>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {trasporti.map((t) => (
                    <div key={t.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                      <div>
                        <p className="font-medium">{t.nome} {t.cognome}</p>
                        {t.telefono && <p className="text-sm text-slate-600">Tel: {t.telefono}</p>}
                        {t.email && <p className="text-sm text-slate-600">Email: {t.email}</p>}
                        {t.indirizzo && <p className="text-sm text-slate-500">{t.indirizzo}</p>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => { 
                          setEditingTrasporto(t); 
                          setTrasportoForm({ 
                            nome: t.nome || "", 
                            cognome: t.cognome || "", 
                            indirizzo: t.indirizzo || "", 
                            telefono: t.telefono || "", 
                            email: t.email || "" 
                          }); 
                        }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDeleteTrasporto(t.id)}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog Gestione Articoli */}
          <Dialog open={showArticoliDialog} onOpenChange={setShowArticoliDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Gestione Tipi Articoli</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <form onSubmit={handleSaveArticolo} className="flex gap-3 p-4 bg-slate-50 rounded-lg">
                  <Input
                    placeholder="Nome articolo (es: Serramenti, Zanzariere)"
                    value={articoloForm.nome}
                    onChange={(e) => setArticoloForm({ nome: e.target.value })}
                    required
                    className="flex-1"
                  />
                  <Button type="submit">{editingArticolo ? "Aggiorna" : "Aggiungi"}</Button>
                  {editingArticolo && (
                    <Button type="button" variant="outline" onClick={() => { setEditingArticolo(null); setArticoloForm({ nome: "" }); }}>
                      Annulla
                    </Button>
                  )}
                </form>
                <div className="space-y-2">
                  {tipoArticoli.map((a) => (
                    <div key={a.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                      <span className="font-medium">{a.nome}</span>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => { setEditingArticolo(a); setArticoloForm({ nome: a.nome }); }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDeleteArticolo(a.id)}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog Gestione Magazzini */}
          <Dialog open={showMagazziniDialog} onOpenChange={setShowMagazziniDialog}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Gestione Magazzini</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <form onSubmit={handleSaveMagazzino} className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <Input
                    placeholder="Nome Deposito *"
                    value={magazzinoForm.nome}
                    onChange={(e) => setMagazzinoForm({ ...magazzinoForm, nome: e.target.value })}
                    required
                  />
                  <Input
                    placeholder="Indirizzo"
                    value={magazzinoForm.indirizzo}
                    onChange={(e) => setMagazzinoForm({ ...magazzinoForm, indirizzo: e.target.value })}
                  />
                  <Input
                    placeholder="Telefono"
                    value={magazzinoForm.telefono}
                    onChange={(e) => setMagazzinoForm({ ...magazzinoForm, telefono: e.target.value })}
                  />
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1">{editingMagazzino ? "Aggiorna" : "Aggiungi"}</Button>
                    {editingMagazzino && (
                      <Button type="button" variant="outline" onClick={() => { setEditingMagazzino(null); setMagazzinoForm({ nome: "", indirizzo: "", telefono: "" }); }}>
                        Annulla
                      </Button>
                    )}
                  </div>
                </form>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {magazzini.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">Nessun magazzino registrato</p>
                  ) : (
                    magazzini.map((m) => (
                      <div key={m.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                        <div>
                          <p className="font-medium">{m.nome}</p>
                          {m.telefono && <p className="text-sm text-slate-600">Tel: {m.telefono}</p>}
                          {m.indirizzo && <p className="text-sm text-slate-500">{m.indirizzo}</p>}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => { 
                            setEditingMagazzino(m); 
                            setMagazzinoForm({ 
                              nome: m.nome || "", 
                              indirizzo: m.indirizzo || "", 
                              telefono: m.telefono || "" 
                            }); 
                          }}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteMagazzino(m.id)}>
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog Nuova Commessa */}
          <Dialog open={showNewCommessaDialog} onOpenChange={setShowNewCommessaDialog}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Crea Nuova Commessa</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 py-4">
                {/* Selezione Cliente */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Selezione Cliente</Label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={clienteManuale}
                        onChange={(e) => {
                          setClienteManuale(e.target.checked);
                          setSelectedClientePagamenti(null);
                          setNewCommessaForm({
                            ...newCommessaForm,
                            cliente_nome: "",
                            cliente_cognome: "",
                            cliente_telefono: "",
                            indirizzo: ""
                          });
                        }}
                        className="w-4 h-4"
                      />
                      <span>Cliente non presente in anagrafica pagamenti</span>
                    </label>
                  </div>

                  {!clienteManuale ? (
                    <>
                      <div>
                        <Label>Cliente da Anagrafica Pagamenti</Label>
                        <Input
                          type="text"
                          placeholder="Cerca per cognome, nome o telefono..."
                          value={clienteSearchTerm}
                          onChange={(e) => setClienteSearchTerm(e.target.value)}
                          className="mb-2"
                          data-testid="search-cliente-input"
                        />
                        {(() => {
                          const filteredSortedClienti = clientiPagamenti
                            .filter((c) => {
                              if (!clienteSearchTerm.trim()) return true;
                              const q = clienteSearchTerm.toLowerCase();
                              return `${c.cognome || ""} ${c.nome || ""} ${c.telefono || ""}`.toLowerCase().includes(q);
                            })
                            .sort((a, b) => (a.cognome || "").localeCompare(b.cognome || "", "it", { sensitivity: "base" }));
                          return (
                            <Select 
                              value={selectedClientePagamenti || ""} 
                              onValueChange={async (value) => {
                                const cliente = filteredSortedClienti.find(c => `${c.nome}_${c.cognome}` === value)
                                  || clientiPagamenti.find(c => `${c.nome}_${c.cognome}` === value);
                                if (cliente) {
                                  setSelectedClientePagamenti(value);
                                  setNewCommessaForm({
                                    ...newCommessaForm,
                                    cliente_nome: cliente.nome || "",
                                    cliente_cognome: cliente.cognome || "",
                                    cliente_telefono: cliente.telefono || "",
                                    indirizzo: cliente.indirizzo || ""
                                  });
                                  setClienteSearchTerm("");
                                }
                              }}
                            >
                              <SelectTrigger data-testid="select-cliente">
                                <SelectValue placeholder={`Seleziona un cliente... (${filteredSortedClienti.length} risultati, ordinati per cognome)`} />
                              </SelectTrigger>
                              <SelectContent className="max-h-72">
                                {filteredSortedClienti.length === 0 ? (
                                  <div className="px-2 py-3 text-sm text-slate-500 text-center">
                                    Nessun cliente trovato
                                  </div>
                                ) : (
                                  filteredSortedClienti.map((c) => (
                                    <SelectItem key={`${c.nome}_${c.cognome}`} value={`${c.nome}_${c.cognome}`}>
                                      {c.cognome} {c.nome}{c.telefono ? ` — ${c.telefono}` : ' — (Tel non disponibile)'}
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          );
                        })()}
                        <p className="text-xs text-slate-500 mt-1">
                          {clientiPagamenti.length} client{clientiPagamenti.length !== 1 ? 'i' : 'e'} disponibil{clientiPagamenti.length !== 1 ? 'i' : 'e'} dall&apos;anagrafica pagamenti
                        </p>
                      </div>

                      {selectedClientePagamenti && (
                        <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                          <div>
                            <Label className="text-xs text-slate-600">Nome</Label>
                            <p className="font-medium">{newCommessaForm.cliente_nome}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Cognome</Label>
                            <p className="font-medium">{newCommessaForm.cliente_cognome}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Telefono</Label>
                            <p className="font-medium">{newCommessaForm.cliente_telefono || 'Non disponibile'}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Indirizzo</Label>
                            <p className="font-medium">{newCommessaForm.indirizzo || 'Non disponibile'}</p>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <div className="text-sm text-yellow-800">
                          <p className="font-semibold">⚠️ Cliente non in anagrafica pagamenti</p>
                          <p className="mt-1">I dati pagamenti dovranno essere inseriti manualmente nella sezione Pagamenti dopo la creazione della commessa.</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Nome</Label>
                          <Input
                            value={newCommessaForm.cliente_nome}
                            onChange={(e) => setNewCommessaForm({ ...newCommessaForm, cliente_nome: e.target.value })}
                            placeholder="Mario"
                            required
                          />
                        </div>
                        <div>
                          <Label>Cognome</Label>
                          <Input
                            value={newCommessaForm.cliente_cognome}
                            onChange={(e) => setNewCommessaForm({ ...newCommessaForm, cliente_cognome: e.target.value })}
                            placeholder="Rossi"
                            required
                          />
                        </div>
                        <div>
                          <Label>Telefono</Label>
                          <Input
                            value={newCommessaForm.cliente_telefono}
                            onChange={(e) => setNewCommessaForm({ ...newCommessaForm, cliente_telefono: e.target.value })}
                            placeholder="3331234567"
                            required
                          />
                        </div>
                        <div>
                          <Label>Indirizzo</Label>
                          <Input
                            value={newCommessaForm.indirizzo}
                            onChange={(e) => setNewCommessaForm({ ...newCommessaForm, indirizzo: e.target.value })}
                            placeholder="Via Roma 123"
                            required
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Articoli */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Articoli da Ordinare</Label>
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setNewCommessaForm({
                          ...newCommessaForm,
                          articoli: [...newCommessaForm.articoli, { 
                            tipo_articolo_id: "", 
                            tipo_articolo_nome: "",
                            trasmesso: false, 
                            data_prevista_ordine: "",
                            data_arrivo_deposito: "" 
                          }]
                        });
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Aggiungi Articolo
                    </Button>
                  </div>
                  
                  {newCommessaForm.articoli.map((articolo, idx) => (
                    <div key={idx} className="flex gap-3 items-start p-3 border rounded-lg bg-white">
                      <div className="flex-1 space-y-3">
                        <Select
                          value={articolo.tipo_articolo_id}
                          onValueChange={(value) => {
                            const tipo = tipoArticoli.find(t => t.id === value);
                            const newArticoli = [...newCommessaForm.articoli];
                            newArticoli[idx] = { 
                              ...newArticoli[idx], 
                              tipo_articolo_id: value,
                              tipo_articolo_nome: tipo?.nome || ""
                            };
                            setNewCommessaForm({ ...newCommessaForm, articoli: newArticoli });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona tipo articolo..." />
                          </SelectTrigger>
                          <SelectContent>
                            {tipoArticoli.map((t) => (
                              <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Data Arrivo Deposito</Label>
                            <Input 
                              type="date"
                              value={articolo.data_arrivo_deposito}
                              onChange={(e) => {
                                const newArticoli = [...newCommessaForm.articoli];
                                newArticoli[idx] = { ...newArticoli[idx], data_arrivo_deposito: e.target.value };
                                setNewCommessaForm({ ...newCommessaForm, articoli: newArticoli });
                              }}
                            />
                          </div>
                          <div className="flex items-end">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={articolo.trasmesso}
                                onChange={(e) => {
                                  const newArticoli = [...newCommessaForm.articoli];
                                  newArticoli[idx] = { ...newArticoli[idx], trasmesso: e.target.checked };
                                  setNewCommessaForm({ ...newCommessaForm, articoli: newArticoli });
                                }}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Trasmesso</span>
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      <Button 
                        type="button"
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          const newArticoli = newCommessaForm.articoli.filter((_, i) => i !== idx);
                          setNewCommessaForm({ ...newCommessaForm, articoli: newArticoli });
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  ))}
                  
                  {newCommessaForm.articoli.length === 0 && (
                    <div className="text-center py-6 text-slate-500 border-2 border-dashed rounded-lg">
                      <Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">Nessun articolo aggiunto</p>
                    </div>
                  )}
                </div>

                {/* Info Box Pagamenti */}
                {(selectedClientePagamenti || (!clienteManuale && newCommessaForm.cliente_nome)) && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-semibold">ℹ️ Informazioni Pagamenti</p>
                        <p className="mt-1">I dati relativi ai pagamenti (anticipo, saldo, contabili) saranno recuperati automaticamente dalla sezione <strong>Pagamenti</strong> una volta creata la commessa.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Azioni */}
                <div className="flex gap-3 justify-end pt-4 border-t">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowNewCommessaDialog(false);
                      setClienteManuale(false);
                      setSelectedClientePagamenti(null);
                      setNewCommessaForm({
                        intervista_id: null,
                        cliente_nome: "",
                        cliente_cognome: "",
                        cliente_telefono: "",
                        indirizzo: "",
                        articoli: []
                      });
                    }}
                  >
                    Annulla
                  </Button>
                  <Button 
                    onClick={handleCreateCommessa}
                    disabled={!newCommessaForm.cliente_nome || !newCommessaForm.cliente_cognome || newCommessaForm.articoli.length === 0}
                    className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
                  >
                    Crea Commessa
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
    </PageLayout>
  );
}